import { Injectable } from '@angular/core';

import {
  isAfter,
  isBefore,
  isEqual,
  parseISO,
  startOfDay
} from 'date-fns';

import {
  BehaviorSubject,
  catchError,
  filter,
  forkJoin,
  map,
  Observable,
  of,
  Subscription,
  switchMap,
  take
} from 'rxjs';

import {
  getTodayAsLocalDate,
  isInsuranceVerified,
  IPatientInsurance,
  parseLocalDate,
  PatientInsuranceStore
} from '@sondermind/data-access/client-insurance';

import { CurrentUser } from '@sondermind/data-access/client-user';
import { MemberProviderNetworkCompanyService } from '@sondermind/data-access/provider-network-company';
import { CoverageNotificationType } from
  '../components/coverage-notification-banner/coverage-notification.types';

@Injectable({
  providedIn: 'root'
})
export class CoverageNotificationService {
  private showBannerSubject = new BehaviorSubject<boolean>(false);
  private notificationTypeSubject = new BehaviorSubject<CoverageNotificationType>(CoverageNotificationType.EXPIRED);
  private coverageStartDateSubject = new BehaviorSubject<string | null>(null);
  private hasInactiveInsuranceSubject = new BehaviorSubject<boolean>(false);
  private hasUnverifiedInsuranceSubject = new BehaviorSubject<boolean>(false);
  private unverifiedInsurancesSubject = new BehaviorSubject<IPatientInsurance[]>([]);
  private hasExpiredInsuranceSubject = new BehaviorSubject<boolean>(false);
  private expiredInsurancesSubject = new BehaviorSubject<IPatientInsurance[]>([]);
  private hasCashPayInsuranceSubject = new BehaviorSubject<boolean>(false);
  private hasValidInsuranceSubject = new BehaviorSubject<boolean>(false);
  private validInsurancesSubject = new BehaviorSubject<IPatientInsurance[]>([]);
  private skipEarlyBvInsurancesSubject = new BehaviorSubject<IPatientInsurance[]>([]);
  private hasEapWithLowSessionsSubject = new BehaviorSubject<boolean>(false);
  private hasEapOutOfSessionsSubject = new BehaviorSubject<boolean>(false);
  private hasEapExpiringSoonSubject = new BehaviorSubject<boolean>(false);
  private hasEapExpiredSubject = new BehaviorSubject<boolean>(false);
  private hasOutOfNetworkProvidersSubject = new BehaviorSubject<boolean>(false);

  showBanner$ = this.showBannerSubject.asObservable();
  notificationType$ = this.notificationTypeSubject.asObservable();
  coverageStartDate$ = this.coverageStartDateSubject.asObservable();
  hasInactiveInsurance$ = this.hasInactiveInsuranceSubject.asObservable();
  hasUnverifiedInsurance$ = this.hasUnverifiedInsuranceSubject.asObservable();
  unverifiedInsurances$ = this.unverifiedInsurancesSubject.asObservable();
  hasExpiredInsurance$ = this.hasExpiredInsuranceSubject.asObservable();
  expiredInsurances$ = this.expiredInsurancesSubject.asObservable();
  hasCashPayInsurance$ = this.hasCashPayInsuranceSubject.asObservable();
  hasValidInsurance$ = this.hasValidInsuranceSubject.asObservable();
  validInsurances$ = this.validInsurancesSubject.asObservable();
  skipEarlyBvInsurances$ = this.skipEarlyBvInsurancesSubject.asObservable();
  hasEapWithLowSessions$ = this.hasEapWithLowSessionsSubject.asObservable();
  hasEapOutOfSessions$ = this.hasEapOutOfSessionsSubject.asObservable();
  hasEapExpiringSoon$ = this.hasEapExpiringSoonSubject.asObservable();
  hasEapExpired$ = this.hasEapExpiredSubject.asObservable();
  hasOutOfNetworkProviders$ = this.hasOutOfNetworkProvidersSubject.asObservable();

  private fullInsuranceListSubscription: Subscription;

  constructor(private currentUser: CurrentUser,
    private memberProviderNetworkService: MemberProviderNetworkCompanyService,
    private patientInsuranceStore: PatientInsuranceStore
  ) {}

  /**
   * Cancels patientInsuranceStore.fullInsuranceList$ subscription
   */
  cancelFullInsuranceListSubscription(): void {
    this.fullInsuranceListSubscription?.unsubscribe();
  }

  checkCoverageNotification(force = false): void {
    // if already have a subscription, prevent duplication
    if (this.fullInsuranceListSubscription) {
      if (force) {
        this.cancelFullInsuranceListSubscription();
      } else {
        return;
      }
    }

    // note that this is a perpetual subscription (rather than one-time via take(1)) to allow
    // consumers of the state observables (e.g. validInsurances$, hasUnverifiedInsurance$, etc), to
    // update as user's insurance info changes
    this.fullInsuranceListSubscription = this.patientInsuranceStore.fullInsuranceList$
      .pipe(
        filter((patientInsurances) => patientInsurances && patientInsurances.length > 0)
      )
      .subscribe({
        next: (patientInsurances: IPatientInsurance[]) => {
          // If there are no insurances or all insurances are valid, hide the banner
          const hasValidInsurance = this.hasValidInsurance(patientInsurances);
          this.hasValidInsuranceSubject.next(hasValidInsurance);

          const hasCashPayInsurance = patientInsurances.some(
            (insurance) => insurance.providerNetworkCompany?.isCashPay
          );
          // TODO: Uncomment this when we have backfilled clients self-pay insurances who have payment methods in CASH-1514
          // const shouldShowAddPaymentMethod = this.shouldShowAddPaymentMethod(patientInsurances, hasCashPayInsurance);

          // // ADD_PAYMENT_METHOD takes priority over other warnings/alerts
          // if (shouldShowAddPaymentMethod) {
          //   this.showBanner(CoverageNotificationType.ADD_PAYMENT_METHOD);
          //   return;
          // }

          if (!patientInsurances || patientInsurances.length === 0 || hasValidInsurance) {
            this.hideBanner();
            return;
          }

          let clientInsurances = patientInsurances;

          if (this.currentUser.isMultipayor) {
            clientInsurances = [patientInsurances.find((ins) => ins.insuranceInformation?.ranking === 1) || null];

            if (!clientInsurances || clientInsurances.length === 0) {
              this.hideBanner();
              return;
            }
          }

          // Check all insurances and determine the appropriate notification type
          const hasInactiveInsurance = patientInsurances.some((insurance) => this.isCoverageNotActiveYet(insurance));
          const unverifiedInsurances = patientInsurances.filter((insurance) => this.isCoverageUnverified(insurance));
          const expiredInsurances = patientInsurances.filter((insurance) => this.isInsuranceExpired(insurance));
          const hasSkipEarlyBvInsurance = patientInsurances.some(
            (insurance) => insurance.providerNetworkCompany?.skipEarlyBv && !insurance.providerNetworkCompany?.isCashPay
          );
          const hasEapWithLowSessions = patientInsurances.some((insurance) => this.isEapWithLowSessions(insurance));
          const hasEapOutOfSessions = patientInsurances.some((insurance) => this.isEapOutOfSessions(insurance));
          const hasEapExpiringSoon = patientInsurances.some((insurance) => this.isEapExpiringSoon(insurance));
          const hasEapExpired = patientInsurances.some((insurance) => this.isEapExpired(insurance));

          this.hasInactiveInsuranceSubject.next(hasInactiveInsurance);
          this.hasUnverifiedInsuranceSubject.next(unverifiedInsurances.length > 0);
          this.unverifiedInsurancesSubject.next(unverifiedInsurances);
          this.hasExpiredInsuranceSubject.next(expiredInsurances.length > 0);
          this.expiredInsurancesSubject.next(expiredInsurances);
          this.hasCashPayInsuranceSubject.next(hasCashPayInsurance);
          this.hasEapWithLowSessionsSubject.next(hasEapWithLowSessions);
          this.hasEapOutOfSessionsSubject.next(hasEapOutOfSessions);
          this.hasEapExpiringSoonSubject.next(hasEapExpiringSoon);
          this.hasEapExpiredSubject.next(hasEapExpired);

          // Show banner based on priority order
          if (hasEapExpired && !hasCashPayInsurance) {
            this.showBanner(CoverageNotificationType.EAP_EXPIRED);
          } else if (hasEapOutOfSessions && !hasCashPayInsurance) {
            this.showBanner(CoverageNotificationType.EAP_SESSIONS_OUT_OF);
          } else if (hasEapWithLowSessions && !hasCashPayInsurance) {
            this.showBanner(CoverageNotificationType.EAP_SESSIONS_LOW);
          } else if (hasEapExpiringSoon && !hasCashPayInsurance) {
            this.showBanner(CoverageNotificationType.EAP_EXPIRING_SOON);
          } else if (hasInactiveInsurance) {
            this.showBanner(CoverageNotificationType.NO_ACTIVE_COVERAGE);
          } else if (expiredInsurances.length > 0) {
            this.showBanner(CoverageNotificationType.EXPIRED);
          } else if (unverifiedInsurances.length > 0) {
            this.showBanner(CoverageNotificationType.COVERAGE_UNVERIFIED);
          } else if (hasCashPayInsurance && !hasSkipEarlyBvInsurance) {
            this.showBanner(CoverageNotificationType.NO_COVERAGE);
          } else {
            this.hideBanner();
          }
        },
        error: () => {
          this.hideBanner();
        }
      });
  }

  checkOutOfNetworkProviders(providerIds: number[]): Observable<boolean> {
    return this.patientInsuranceStore.fullInsuranceList$
      .pipe(
        filter((patientInsurances) => patientInsurances && patientInsurances.length > 0),
        take(1),
        switchMap((insurances) => {
          if (!providerIds || providerIds.length === 0) {
            this.hasOutOfNetworkProvidersSubject.next(false);
            return of(false);
          }

          const filterOptions = [{ key: 'approved_only', value: 'true' }];

          const requests = providerIds.map((id) => this.memberProviderNetworkService
            .index({
              filter: [
                ...filterOptions,
                { key: 'persona_provider_id', value: id.toString() }
              ],
              page: 'disabled',
            })
            .pipe(map((res) => res.data))
          );

          return forkJoin(requests)
            .pipe(
              take(1),
              map((providerNetworkCompanies) => {
                // Build a set of the client's provider network company IDs from all insurances
                const clientProviderNetworkCompanyIds = new Set<number>();
                insurances.forEach((insurance) => {
                  const fromCompany = insurance?.providerNetworkCompany?.id;
                  clientProviderNetworkCompanyIds.add(fromCompany);
                });

                // Determine if any provider is out-of-network relative to the client's insurances
                const hasOutOfNetworkProvider = providerNetworkCompanies.some((companiesForProvider) => {
                  if (!companiesForProvider || companiesForProvider.length === 0) {
                    return true;
                  }

                  const hasMatchingCompany = companiesForProvider.some((memberCompany) => {
                    const providerCompanyId = memberCompany?.providerNetworkCompany?.id;
                    return clientProviderNetworkCompanyIds.has(providerCompanyId);
                  });

                  return !hasMatchingCompany;
                });

                this.hasOutOfNetworkProvidersSubject.next(hasOutOfNetworkProvider);
                if (hasOutOfNetworkProvider) {
                  this.showBanner(CoverageNotificationType.OUT_OF_NETWORK);
                }

                return hasOutOfNetworkProvider;
              }),
              catchError(() => {
                this.hasOutOfNetworkProvidersSubject.next(false);
                return of(false);
              })
            );
        })
      );
  }

  hideBanner(): void {
    this.showBannerSubject.next(false);
  }

  private showBanner(coverageNotificationType: CoverageNotificationType): void {
    this.showBannerSubject.next(true);
    this.notificationTypeSubject.next(coverageNotificationType);
  }

  private shouldShowAddPaymentMethod(
    patientInsurances: IPatientInsurance[],
    hasCashPayInsurance: boolean
  ): boolean {
    if (!hasCashPayInsurance) {
      // Check if there are any regular insurances (non-EAP, non-cash pay, non-usage based, non-veterans affairs, non-skipEarlyBv)
      // Only consider insurances that have a providerNetworkCompany defined
      const regularInsurances = patientInsurances.filter((insurance) => insurance.providerNetworkCompany
          && !insurance.providerNetworkCompany.isCashPay
          && !insurance.providerNetworkCompany.eap
          && !insurance.providerNetworkCompany.isUsageBased
          && !insurance.providerNetworkCompany.veteransAffairs
          && !insurance.providerNetworkCompany.skipEarlyBv);

      return regularInsurances.length > 0;
    }

    return false;
  }

  private isInsuranceExpired(insurance: IPatientInsurance): boolean {
    if (insurance?.benefitVerification?.verified || insurance?.providerNetworkCompany?.skipEarlyBv) {
      return false;
    }

    if (!insurance?.benefitVerification?.coverageEndDate) {
      return false;
    }

    try {
      const endDate = parseISO(insurance?.benefitVerification?.coverageEndDate);
      const today = startOfDay(new Date());

      return isAfter(today, endDate);
    } catch (error) {
      return true;
    }
  }

  private isCoverageUnverified(insurance: IPatientInsurance): boolean {
    if (insurance.providerNetworkCompany?.isCashPay || insurance.providerNetworkCompany?.skipEarlyBv) {
      return false;
    }

    return !isInsuranceVerified(insurance);
  }

  private isCoverageNotActiveYet(insurance: IPatientInsurance): boolean {
    if (insurance?.benefitVerification?.verified || insurance?.providerNetworkCompany?.skipEarlyBv) {
      return false;
    }

    if (!insurance?.benefitVerification?.coverageStartDate) {
      return false;
    }

    try {
      this.coverageStartDateSubject.next(insurance?.benefitVerification?.coverageStartDate);
      const startDate = parseISO(insurance?.benefitVerification?.coverageStartDate);
      const today = startOfDay(new Date());

      return isAfter(startDate, today);
    } catch (error) {
      return true;
    }
  }

  /**
   * Checks if an insurance is an EAP and has low sessions (2 or less).
   *
   * @param insurance The insurance object to check
   * @returns true if the insurance is an EAP with 2 or fewer sessions remaining, false otherwise
   */
  private isEapWithLowSessions(insurance: IPatientInsurance): boolean {
    // Check if it's an EAP insurance
    if (!insurance?.providerNetworkCompany?.eap) {
      return false;
    }

    // Check if eapSessionsRemaining is defined and is less than 3
    return insurance.eapSessionsRemaining > 0 && insurance.eapSessionsRemaining < 3;
  }

  /**
   * Checks if an insurance is an EAP and is completely out of sessions.
   *
   * @param insurance The insurance object to check
   * @returns true if the insurance is an EAP with 0 sessions remaining, false otherwise
   */
  private isEapOutOfSessions(insurance: IPatientInsurance): boolean {
    // Check if it's an EAP insurance
    if (!insurance?.providerNetworkCompany?.eap) {
      return false;
    }

    // Check if eapSessionsRemaining is 0
    return insurance.eapSessionsRemaining === 0;
  }

  /**
   * Checks if an insurance is an EAP and is expiring soon (within 1 month).
   *
   * @param insurance The insurance object to check
   * @returns true if the insurance is an EAP and is expiring soon, false otherwise
   */
  private isEapExpiringSoon(insurance: IPatientInsurance): boolean {
    if (!insurance?.providerNetworkCompany?.eap || !insurance.insuranceInformation?.eapEndDate) {
      return false;
    }

    const endDate = parseLocalDate(insurance.insuranceInformation?.eapEndDate as string);
    const today = getTodayAsLocalDate();
    const oneMonthFromToday = getTodayAsLocalDate();
    oneMonthFromToday.setMonth(today.getMonth() + 1);

    // Don't return true if the end date is in the past
    if (endDate < today) {
      return false;
    }

    // If end date is within 1 month from today, it's expiring soon
    return (endDate.getTime() === today.getTime()) || (endDate <= oneMonthFromToday);
  }

  /**
   * Checks if an insurance is an EAP and is expired.
   *
   * @param insurance The insurance object to check
   * @returns true if the insurance is an EAP and is expired, false otherwise
   */
  private isEapExpired(insurance: IPatientInsurance): boolean {
    if (!insurance?.providerNetworkCompany?.eap || !insurance.insuranceInformation?.eapEndDate) {
      return false;
    }

    const expirationDate = parseLocalDate(insurance.insuranceInformation?.eapEndDate as string);
    const today = getTodayAsLocalDate();

    // EAP is expired if today is after the expiration date (expiration date is in the past)
    return today > expirationDate;
  }

  /**
   * Checks if any of the patient insurances are currently valid.
   * A valid insurance must have verified=true and today's date must be within
   * the coverageStartDate and coverageEndDate range.
   *
   * @param patientInsurances Array of patient insurance objects to check
   * @returns true if at least one insurance is currently valid, false otherwise
   */
  hasValidInsurance(patientInsurances: IPatientInsurance[]): boolean {
    // If there are no insurances, there are no valid insurances
    if (!patientInsurances || patientInsurances.length === 0) {
      return false;
    }

    const validInsurances = patientInsurances.filter((insurance) => isInsuranceVerified(insurance));

    this.validInsurancesSubject.next(validInsurances);

    // Track skip-early-BV plans (excluding cash pay) so the in-network match in coverage-badge.store
    // can find them. This is emitted before the early return below because it must stay accurate even
    // when the patient also has a verified insurance. A plan can be both EAP and skip-early-BV (e.g.
    // Headspace EAP/Care); such plans were previously dropped here and wrongly shown as out-of-network
    // (MER-4858), so they are intentionally included.
    const skipEarlyBvInsurances = patientInsurances.filter((insurance) => this.isSkipEarlyBvInsurance(insurance));
    this.skipEarlyBvInsurancesSubject.next(skipEarlyBvInsurances);

    if (validInsurances.length > 0) {
      return true;
    }

    const today = startOfDay(new Date());
    let allInsurancesAreValid: boolean = true;

    // Iterate through all insurances and check if they are verified.
    // If they are verified, check if the coverageStartDate and coverageEndDate cover today's date.
    patientInsurances.forEach((insurance) => {
      // EAP plans are never treated as a "valid insurance" that suppresses coverage banners; their
      // session/expiry state drives the dedicated EAP banners in checkCoverageNotification.
      if (insurance.providerNetworkCompany?.eap) {
        allInsurancesAreValid = false;
        return;
      }

      // Non-EAP skip-early-BV plans are treated as valid; skip the verification checks below.
      if (this.isSkipEarlyBvInsurance(insurance)) {
        return; // Skip to next insurance
      }

      // Insurance must be verified
      if (!insurance?.benefitVerification?.verified) {
        allInsurancesAreValid = false;
        return;
      }

      // Check to ensure both start and end dates are not null
      if (!insurance?.benefitVerification?.coverageStartDate || !insurance?.benefitVerification?.coverageEndDate) {
        allInsurancesAreValid = false;
        return; // Skip to next insurance
      }

      try {
        const startDate = parseISO(insurance?.benefitVerification?.coverageStartDate);
        const endDate = parseISO(insurance?.benefitVerification?.coverageEndDate);

        // Today must be on or after start date and on or before end date
        const isAfterOrEqualStart = isAfter(today, startDate) || isEqual(today, startDate);
        const isBeforeOrEqualEnd = isBefore(today, endDate) || isEqual(today, endDate);

        if (!isAfterOrEqualStart || !isBeforeOrEqualEnd) {
          allInsurancesAreValid = false;
        }
      } catch (error) {
        // If date parsing fails, consider this insurance invalid
        allInsurancesAreValid = false;
      }
    });

    return allInsurancesAreValid;
  }

  /**
   * Whether an insurance is a skip-early-BV plan eligible for in-network matching.
   * Cash pay plans are excluded because they are never treated as in-network coverage.
   *
   * @param insurance The insurance object to check
   * @returns true if the insurance is skip-early-BV and not cash pay, false otherwise
   */
  private isSkipEarlyBvInsurance(insurance: IPatientInsurance): boolean {
    return !!insurance.providerNetworkCompany?.skipEarlyBv && !insurance.providerNetworkCompany?.isCashPay;
  }
}
